import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "../assets/custom-css/bac-score.css";
import {
    SECTIONS,
    FORM_VALIDATION,
    SCORE_FORMULAS,
    FILTERED_STREAMS,
} from "../constants/bac-score-helpers";
import { useForm } from "react-hook-form";

const SESSIONS = ["main", "retake"];

const BacScore = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language?.startsWith("ar");
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [selected, setSelected] = useState("economie_gestion");
    const [selectedSession, setSelectedSession] = useState("main");
    const [sportDispensed, setSportDispensed] = useState(false);
    const [score, setScore] = useState("");
    const [scoreWithBonus, setScoreWithBonus] = useState("");
    const [showResults, setShowResults] = useState(false);
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        mode: "onChange",
    });
    const formatScore = (value) => Number(value).toFixed(2);

    const calculateScore = (data) => {
        const sectionLabel = SECTIONS.find((section) => section.key === selected)
            ?.label;
        const formula = SCORE_FORMULAS[sectionLabel];

        if (!formula) {
            setScore("");
            setScoreWithBonus("");
            return;
        }

        const averageValue = toNumber(data.average);
        const gradesBySubject = FILTERED_STREAMS[selected].reduce(
            (acc, { subject }) => {
                if (subject === "Sport" && sportDispensed) {
                    acc[subject] = 0;
                    return acc;
                }

                acc[subject] = toNumber(data[subject]);
                return acc;
            },
            {},
        );

        const simpleScore = formula(averageValue, gradesBySubject);

        setScore(formatScore(simpleScore));
        setScoreWithBonus(formatScore(simpleScore * 1.07));
    };
    const toNumber = (value) => {
        if (value === null || value === undefined || value === "") {
            return 0;
        }

        return Number(String(value).replace(",", "."));
    };
    const onSubmit = (data) => {

        calculateScore(data);
        if (isMobile) {
            setShowResults(true);
        }
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        // Reset form and scores when section changes
        reset();
        setScore("");
        setScoreWithBonus("");
        setSportDispensed(false);
    }, [selected, reset]);

    useEffect(() => {
        // Reset form and scores when session changes
        reset();
        setScore("");
        setScoreWithBonus("");
        setSportDispensed(false);
    }, [selectedSession, reset]);

    return (
        <>
            <div className="pd-top-150">
                <div
                    className={`container bac-score-page ${isRTL ? "bac-score-page-rtl" : "bac-score-page-ltr"
                        }`}
                    //dir={isRTL ? "rtl" : "ltr"}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "40px",
                    }}
                >
                    <div className="about-area-inner bac-score-form-panel">
                        <div className="row">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="col-lg-12 about-animate-right pb-0">
                                    <div className="about-inner-wrap pt-5 pt-lg-3 mt-5 mt-lg-0">
                                        <div className="section-title mb-0">
                                            <h2 className="title">{t("bac score page title")}</h2>
                                            <>
                                                <h5 className="mb-4">
                                                    {t("bac score choose section")}
                                                </h5>
                                                <div
                                                    className="d-flex flex-wrap"
                                                    style={{ gap: "12px" }}
                                                >
                                                    {SECTIONS.map((section) => (
                                                        <button
                                                            key={section.key}
                                                            type="button"
                                                            className={`section-btn ${selected === section.key ? "active" : ""
                                                                }`}
                                                            onClick={() => setSelected(section.key)}
                                                        >
                                                            {t(`bac score section ${section.key}`)}
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        </div>
                                        <div className="mt-4">
                                            <h5 className="mb-4">
                                                {t("bac score choose session")}
                                            </h5>
                                            <div
                                                className="d-flex flex-wrap"
                                                style={{ gap: "12px" }}
                                            >
                                                {SESSIONS.map((session) => (
                                                    <button
                                                        key={session}
                                                        type="button"
                                                        className={`section-btn ${selectedSession === session ? "active" : ""
                                                            }`}
                                                        onClick={() => setSelectedSession(session)}
                                                    >
                                                        {t(`bac score session ${session}`)}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="average-container">
                                        <h5 className="mb-4">
                                            {t("bac score general average")}
                                        </h5>

                                        <div className="input-wrapper">
                                            <label className="floating-label">
                                                {t("bac score enter general average")}
                                            </label>

                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                className={`form-control average-input ${errors.average ? "input-error" : ""
                                                    }`}
                                                {...register("average", {
                                                    required: t("bac score validation required"),
                                                    min: {
                                                        value: FORM_VALIDATION.average.min.value,
                                                        message: t("bac score validation average min"),
                                                    },
                                                    max: {
                                                        value: FORM_VALIDATION.average.max.value,
                                                        message: t("bac score validation average max"),
                                                    },
                                                    validate: (value) => {
                                                        const normalized = value.replace(",", ".");
                                                        const number = parseFloat(normalized);

                                                        if (isNaN(number)) {
                                                            return t("bac score validation number");
                                                        }

                                                        if (number < 0) {
                                                            return t("bac score validation grade min");
                                                        }

                                                        if (number > 20) {
                                                            return t("bac score validation grade max");
                                                        }

                                                        return true;
                                                    },
                                                })}

                                            />

                                            {errors.average && (
                                                <small className="error-text">
                                                    {errors.average.message}
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                    <h5 className="my-4">{t("bac score subject grades")}</h5>
                                    {selectedSession === "retake" && (
                                        <h6 className="mb-4">
                                            {t("bac score retake grades note")}
                                        </h6>
                                    )}

                                    <div className="subjects-container">
                                        {FILTERED_STREAMS[selected]?.map((item) => (
                                            <div key={item.subject} className="subjects-item">
                                                <div className="d-flex align-items-center mb-3">
                                                    <label className="subject-title mb-0">
                                                        {t(`bac score subject ${item.subject}`)}
                                                    </label>

                                                    {item.subject === "Sport" && (
                                                        <div className="sport-dispense-control d-flex align-items-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={sportDispensed}
                                                                onChange={(e) =>
                                                                    setSportDispensed(e.target.checked)
                                                                }
                                                            />

                                                            <span
                                                                className={`sport-dispense-label ${sportDispensed ? "active" : ""
                                                                    }`}
                                                            >
                                                                {t("bac score sport dispensed")}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="input-wrapper">
                                                    <input
                                                        type="text"
                                                        inputMode="decimal"
                                                        placeholder={t("bac score grade placeholder")}
                                                        disabled={
                                                            item.subject === "Sport" && sportDispensed
                                                        }
                                                        className={`subject-input ${errors[item.subject] ? "input-error" : ""
                                                            } ${item.subject === "Sport" && sportDispensed
                                                                ? "disabled-input"
                                                                : ""
                                                            }`}
                                                        {...register(item.subject, {
                                                            required:
                                                                item.subject === "Sport" && sportDispensed
                                                                    ? false
                                                                    : t("bac score validation required"),

                                                            min:
                                                                item.subject === "Sport" && sportDispensed
                                                                    ? undefined
                                                                    : {
                                                                        value: FORM_VALIDATION.grade.min.value,
                                                                        message: t(
                                                                            "bac score validation grade min",
                                                                        ),
                                                                    },

                                                            max:
                                                                item.subject === "Sport" && sportDispensed
                                                                    ? undefined
                                                                    : {
                                                                        value: FORM_VALIDATION.grade.max.value,
                                                                        message: t(
                                                                            "bac score validation grade max",
                                                                        ),
                                                                    },

                                                            validate: (value) => {
                                                                const normalized = value.replace(",", ".");
                                                                const number = parseFloat(normalized);

                                                                if (isNaN(number)) {
                                                                    return t("bac score validation number");
                                                                }

                                                                if (number < 0) {
                                                                    return t("bac score validation grade min");
                                                                }

                                                                if (number > 20) {
                                                                    return t("bac score validation grade max");
                                                                }

                                                                return true;
                                                            }
                                                        })}
                                                    />

                                                    {errors[item.subject] && (
                                                        <small className="error-text">
                                                            {errors[item.subject]?.message}
                                                        </small>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                        <button type="submit" className="calculate-btn" style={{ marginBottom: i18n.language === "fr" && !showResults ? "50px" : "0" }}>
                                            {t("bac score calculate")}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                    {(!isMobile || showResults) && (
                        <div className="bac-score-result-panel" style={{ marginBottom: isMobile ? "80px" : "0" }}>
                            <div className="result-card">
                                <div className="result-row">
                                    <span className="result-label">{t("bac score my score")}</span>

                                    <div className="result-value">{score || "--"}</div>
                                </div>

                                <div className="result-row">
                                    <span className="result-label">
                                        {t("bac score my score with bonus")}
                                    </span>

                                    <div className="result-value">{scoreWithBonus || "--"}</div>
                                </div>

                                {/*  <div className="text-center mt-4">
                  <button className="pdf-btn" disabled={!score}>
                    {t("bac score download pdf")}
                  </button>
                </div> */}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default BacScore;
